import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/primitives";
import { ensureUrlProtocol, type MarketplaceLink } from "@/lib/marketplaces";

// ─────────────────────────────────────────────────────────────────────────────
// ListingBadge — single pill on a feed card showing the scan's listing state.
// Three modes:
//   • "kept"    — no marketplaces; scan is in the collector's closet but not for sale
//   • "listed"  — one or more marketplaces; tapping expands a popover of links
//   • "self"    — owner viewing their own scan; tapping fires onEdit instead so
//                 the collector can manage links / kept state / unpost
//
// Design intent: the badge reads at a glance ("Kept" or "Listed · 2") and only
// reveals the link list when the user wants it. Mirrors the editorial feel of
// the rest of the feed card.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  links: MarketplaceLink[];
  /** When true, tapping the badge calls onEdit instead of expanding links. */
  isOwner?: boolean;
  /** When true, the badge renders as a SOLD pill instead of Listed/Kept. */
  sold?: boolean;
  onEdit?: () => void;
  /** Optional analytics hook when a shopper taps an outbound link. */
  onLinkClick?: (name: string) => void;
}

export function ListingBadge({ links, isOwner, sold, onEdit, onLinkClick }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const isKept = links.length === 0;

  // Click-away closer for the popover.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isOwner) { onEdit?.(); return; }
    if (sold) return; // shoppers can't open a sold popover
    if (isKept) return; // non-owners get no interaction on a kept badge
    setOpen((v) => !v);
  };

  // Visual variants — kept = cream/ink (quiet), listed = ink/cream (loud).
  const bg = sold ? "hsl(var(--rust))" : isKept ? "hsl(var(--cream))" : "hsl(var(--ink))";
  const fg = sold ? "hsl(var(--cream))" : isKept ? "hsl(var(--ink))"   : "hsl(var(--cream))";
  const border = "hsl(var(--ink))";
  const interactive = isOwner || (!isKept && !sold);

  const label = sold
    ? "Sold"
    : isKept
    ? "Kept"
    : links.length === 1 ? `Listed · ${links[0].name}` : `Listed · ${links.length}`;

  return (
    <div ref={wrapRef} style={{ position: "relative", flexShrink: 0, alignSelf: "flex-start" }}>
      <button
        type="button"
        onClick={handleClick}
        aria-label={isOwner ? "Edit listing status" : isKept ? "Kept in closet" : "View marketplace links"}
        aria-expanded={open}
        disabled={!interactive}
        style={{
          padding: "4px 8px",
          border: `1px solid ${border}`,
          background: bg,
          color: fg,
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 9,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          display: "flex", alignItems: "center", gap: 4,
          cursor: interactive ? "pointer" : "default",
        }}
      >
        {!isKept && <span style={{ color: "hsl(var(--marigold))" }}>↗</span>}
        <span>{label}</span>
        {isOwner && <span style={{ opacity: 0.7, marginLeft: 2 }}>{Icon.chev(8, "d")}</span>}
      </button>

      {open && !isOwner && !isKept && !sold && (
        <div
          role="menu"
          style={{
            position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 5,
            minWidth: 180,
            background: "hsl(var(--cream))", border: "1px solid hsl(var(--ink))",
            boxShadow: "3px 3px 0 hsl(var(--ink))",
          }}
        >
          <div className="caps" style={{ padding: "8px 10px 4px", color: "hsl(var(--rust))", fontSize: 8 }}>
            Listed on
          </div>
          {links.map((l) => {
            const href = l.url ? ensureUrlProtocol(l.url) : "";
            const Tag = href ? "a" : "div";
            return (
              <Tag
                key={l.name + l.url}
                {...(href ? { href, target: "_blank", rel: "noopener noreferrer" } : {})}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  if (href) onLinkClick?.(l.name);
                  setOpen(false);
                }}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "8px 10px", borderTop: "1px dashed hsl(var(--sand-deep))",
                  fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 13,
                  color: href ? "hsl(var(--ink))" : "hsl(var(--ink-2))",
                  textDecoration: "none",
                  cursor: href ? "pointer" : "default",
                }}
              >
                <span style={{ fontWeight: 600 }}>{l.name}</span>
                <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: href ? "hsl(var(--rust))" : "hsl(var(--ink-2))", letterSpacing: "0.1em" }}>
                  {href ? "OPEN ↗" : "no link"}
                </span>
              </Tag>
            );
          })}
        </div>
      )}
    </div>
  );
}
